import type { paths } from './gramps-openapi';

type ApiPath = keyof paths & string;
type HttpMethod = 'get' | 'post';

type Operation<P extends ApiPath, M extends HttpMethod> = NonNullable<paths[P][M]>;

type QueryParams<Op> = Op extends { parameters: { query?: infer Q } } ? Q : never;
type PathParams<Op> = Op extends { parameters: { path: infer P } } ? P : never;
type RequestBodyJson<Op> = Op extends { requestBody: { content: { 'application/json': infer B } } } ? B : never;

type ResponseJson<Op> =
  Op extends { responses: { 200: { content: { 'application/json': infer R } } } } ? R :
  Op extends { responses: { 201: { content: { 'application/json': infer R } } } } ? R :
  Op extends { responses: { 202: { content: { 'application/json': infer R } } } } ? R :
  unknown;

type RequestOptions<Op> = {
  token?: string;
  query?: QueryParams<Op> extends never ? never : QueryParams<Op>;
  pathParams?: PathParams<Op> extends never ? never : PathParams<Op>;
  body?: RequestBodyJson<Op> extends never ? never : RequestBodyJson<Op>;
};

function isLocalhostHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

let unauthorizedHandler: ((failedToken: string | undefined) => void) | null = null;

/**
 * Registers a callback invoked whenever any apiGet/apiPost call fails with 401.
 * The token that was rejected is passed along so the handler can refresh the
 * specific profile it belongs to, rather than whichever profile happens to be
 * "active" — those can differ once more than one tree/account is registered.
 */
export function setUnauthorizedHandler(handler: ((failedToken: string | undefined) => void) | null): void {
  unauthorizedHandler = handler;
}

/**
 * For the services that answer with a Gramps token but are not reached through
 * apiGet/apiPost (the tools service): a 401 there is the same stale access
 * token, and gets the same silent refresh.
 */
export function reportUnauthorized(failedToken: string | undefined): void {
  unauthorizedHandler?.(failedToken);
}

function getApiRoot(): string {
  const envBase = (import.meta.env.VITE_GRAMPS_API_BASE_URL as string | undefined)?.trim();
  if (envBase) {
    const base = envBase.replace(/\/+$/, '');
    return base.endsWith('/api') ? base.slice(0, -4) : base;
  }

  return isLocalhostHost(window.location.hostname)
    ? 'http://localhost:5000'
    : 'https://backend.genealhistory.eu';
}

function buildPath<P extends ApiPath, M extends HttpMethod>(
  pathTemplate: P,
  options: RequestOptions<Operation<P, M>>,
): string {
  let path = pathTemplate as string;

  const pathParams = (options.pathParams || {}) as Record<string, string | number | boolean>;
  for (const [key, value] of Object.entries(pathParams)) {
    path = path.replace(`{${key}}`, encodeURIComponent(String(value)));
  }

  const apiRoot = getApiRoot();
  const requestPath = `${apiRoot}${path}`;
  const isAbsolute = /^https?:\/\//i.test(requestPath);
  const url = isAbsolute
    ? new URL(requestPath)
    : new URL(requestPath, window.location.origin);
  const query = (options.query || {}) as Record<string, unknown>;
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((item) => url.searchParams.append(key, String(item)));
    } else {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
}

async function request<P extends ApiPath, M extends HttpMethod>(
  method: M,
  pathTemplate: P,
  options: RequestOptions<Operation<P, M>> = {},
): Promise<ResponseJson<Operation<P, M>> | null> {
  const headers: Record<string, string> = {};
  if (options.token) {
    headers.Authorization = `Bearer ${options.token}`;
  }

  let body: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const response = await fetch(buildPath<P, M>(pathTemplate, options), {
    method: method.toUpperCase(),
    headers,
    body,
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 401) {
      unauthorizedHandler?.(options.token);
    }
    return null;
  }

  if (response.status === 204) {
    return null;
  }

  return (await response.json()) as ResponseJson<Operation<P, M>>;
}

export function apiGet<P extends ApiPath>(
  pathTemplate: P,
  options?: RequestOptions<Operation<P, 'get'>>,
): Promise<ResponseJson<Operation<P, 'get'>> | null> {
  return request<P, 'get'>('get', pathTemplate, options || {});
}

export function apiPost<P extends ApiPath>(
  pathTemplate: P,
  options?: RequestOptions<Operation<P, 'post'>>,
): Promise<ResponseJson<Operation<P, 'post'>> | null> {
  return request<P, 'post'>('post', pathTemplate, options || {});
}
